/**
 * Aspect Admin — Spaces (projects / apartments) management.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, openDrawer, openModal, toast, emptyState, closeOverlay } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { createApartment } from "../apartment_store.js";
import { navigate, editorUrl } from "../router.js";
import { t } from "../i18n.js";
import { SPACES, JOBS } from "../mock_data.js";

let state = { filter: "all", query: "", view: "grid" };

function matches(sp) {
  const q = state.query.trim().toLowerCase();
  if (q && !`${sp.name} ${sp.client}`.toLowerCase().includes(q)) return false;
  if (state.filter === "live") return sp.status === "live";
  if (state.filter === "draft") return sp.status === "draft";
  if (state.filter === "processing") return ["rendering", "analyzing"].includes(sp.status);
  if (state.filter === "attention") return sp.status === "attention";
  return true;
}

function spaceCard(sp) {
  return `
    <article class="ap-panel ap-panel--hover ap-space" data-space="${sp.id}">
      <div class="ap-space__thumb">
        ${icon("spaces", 34)}
        <span class="ap-space__badge">${statusBadge(sp.status)}</span>
      </div>
      <div class="ap-space__body">
        <h3 class="ap-space__name">${escapeHtml(sp.name)}</h3>
        <p class="ap-space__meta">${escapeHtml(sp.client)} · ${sp.rooms} ${t("rooms")} · ${sp.area} m² · ${t("Updated")} ${t(sp.updated)}</p>
        <div class="ap-space__foot">
          <button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-act="open">${icon("cube", 14)} ${t("Open editor")}</button>
          <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-act="detail">${t("Details")}</button>
          <div class="ap-menuwrap">
            <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-act="menu" aria-label="${t("Actions")}">${icon("dots", 16)}</button>
            <div class="ap-pop ap-pop--menu hidden">
              <button type="button" class="ap-pop__item" data-act="share">${icon("external", 14)} ${t("Copy tour link")}</button>
              <button type="button" class="ap-pop__item" data-act="restage">${icon("wand", 14)} ${t("Restage with AI")}</button>
              <button type="button" class="ap-pop__item" data-act="duplicate">${icon("copy", 14)} ${t("Duplicate")}</button>
              <button type="button" class="ap-pop__item ap-pop__item--danger" data-act="archive">${icon("archive", 14)} ${t("Archive")}</button>
            </div>
          </div>
        </div>
      </div>
    </article>
  `;
}

function spaceRow(sp) {
  return `
    <tr data-row data-space="${sp.id}">
      <td>
        <div class="ap-entity">
          <div class="ap-avatar">${icon("spaces", 15)}</div>
          <div>
            <span class="ap-cell-main">${escapeHtml(sp.name)}</span>
            <span class="ap-cell-sub">${escapeHtml(sp.client)}</span>
          </div>
        </div>
      </td>
      <td>${statusBadge(sp.status)}</td>
      <td class="ap-cell-num">${sp.rooms}</td>
      <td class="ap-cell-num">${sp.area} m²</td>
      <td class="ap-cell-num">${sp.tours.toLocaleString()}</td>
      <td>${t(sp.updated)}</td>
      <td class="ap-cell-actions"><button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-act="detail">${t("Details")} ${icon("chevronRight", 12)}</button></td>
    </tr>
  `;
}

function detailDrawer(root, sp) {
  const relatedJobs = JOBS.filter((j) => j.space === sp.name).slice(0, 3);
  openDrawer(root, {
    title: sp.name,
    subtitle: `${sp.client} · ${sp.rooms} ${t("rooms")} · ${sp.area} m²`,
    bodyHtml: `
      <div class="ap-space__thumb" style="border-radius:var(--ap-r);border:1px solid var(--ap-hairline);">${icon("spaces", 40)}</div>
      <div class="ap-drawer__section">
        <h4>${t("Details")}</h4>
        <dl class="ap-def">
          <dt>${t("Status")}</dt><dd>${statusBadge(sp.status)}</dd>
          <dt>${t("Client")}</dt><dd>${escapeHtml(sp.client)}</dd>
          <dt>${t("Tour sessions")}</dt><dd>${sp.tours.toLocaleString()}</dd>
          <dt>${t("Owner")}</dt><dd><span class="ap-avatar ap-avatar--sm" style="display:inline-flex;margin-right:0.4rem;">${sp.owner}</span>${t("Assigned")}</dd>
          <dt>${t("Last updated")}</dt><dd>${t(sp.updated)}</dd>
          <dt>${t("Tour link")}</dt><dd><span class="ap-codechip">aspect.app/t/${sp.id} <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-copy style="width:1.4rem;height:1.4rem;">${icon("copy", 12)}</button></span></dd>
        </dl>
      </div>
      ${relatedJobs.length ? `
      <div class="ap-drawer__section">
        <h4>${t("Recent AI jobs")}</h4>
        <div class="ap-timeline">
          ${relatedJobs
            .map(
              (j) => `
            <div class="ap-timeline__item ${j.status === "done" ? "ap-timeline__item--mute" : ""}">
              <strong>${t(j.type)}</strong> · ${j.model} ${j.conf != null ? `· ${t("Confidence").toLowerCase()} ${j.conf}` : ""}
              <span class="ap-timeline__time">${t(j.started)} · ${statusBadge(j.status)}</span>
            </div>`
            )
            .join("")}
        </div>
      </div>` : ""}
      <div class="ap-drawer__section">
        <h4>${t("Activity")}</h4>
        <div class="ap-timeline">
          <div class="ap-timeline__item">${t("<strong>Tour republished</strong> after material update")}<span class="ap-timeline__time">${t(sp.updated)}</span></div>
          <div class="ap-timeline__item ap-timeline__item--mute">${t("<strong>AI staging</strong> applied Scandinavian theme")}<span class="ap-timeline__time">${t("2 days ago")}</span></div>
          <div class="ap-timeline__item ap-timeline__item--mute">${t("<strong>Space created</strong> from plan upload")}<span class="ap-timeline__time">${t("Jun 12")}</span></div>
        </div>
      </div>
    `,
    footHtml: `
      <button type="button" class="ap-btn" data-drawer-share>${icon("external", 15)} ${t("Share tour")}</button>
      <a class="ap-btn ap-btn--primary" href="${editorUrl(sp.id)}">${icon("cube", 15)} ${t("Open in 3D editor")}</a>
    `,
  });

  const slot = root.querySelector("#ap-overlay-slot");
  slot?.querySelector("[data-copy]")?.addEventListener("click", () => toast(root, t("Tour link copied"), "ok"));
  slot?.querySelector("[data-drawer-share]")?.addEventListener("click", () => toast(root, t("Share link copied to clipboard"), "ok"));
}

function newSpaceModal(root, session) {
  let source = null;
  const panel = openModal(root, {
    title: t("New space"),
    bodyHtml: `
      <p class="ap-note" style="margin:0;">${t("Choose how to start. AI can analyze an uploaded floor plan and build the 3D shell automatically.")}</p>
      <div class="ap-stack" style="gap:0.55rem;">
        ${[
          { id: "upload", icon: "upload", t: t("Upload 2D plan"), d: t("AI analyzes the image, traces walls, and builds the space") },
          { id: "demo", icon: "home", t: t("From demo apartment"), d: t("Furnished Scandinavian studio, ready to customize") },
          { id: "scratch", icon: "pencil", t: t("From scratch"), d: t("Blank shell — trace the layout yourself") },
        ]
          .map(
            (o) => `
          <button type="button" class="ap-btn" data-source="${o.id}" style="justify-content:flex-start;padding:0.85rem 1rem;gap:0.8rem;">
            <span class="ap-kpi__icon" style="flex-shrink:0;">${icon(o.icon, 16)}</span>
            <span style="text-align:left;">
              <strong style="display:block;font-size:0.865rem;">${o.t}</strong>
              <span style="display:block;font-size:0.74rem;color:var(--ap-mist);font-weight:400;">${o.d}</span>
            </span>
          </button>`
          )
          .join("")}
      </div>
      <div class="ap-field">
        <label class="ap-field__label" for="sp-name">${t("Space name")}</label>
        <input class="ap-input" id="sp-name" type="text" placeholder="e.g. Marina Tower 12F" value="${t("Untitled space")}" />
      </div>
    `,
    footHtml: `
      <button type="button" class="ap-btn ap-btn--ghost" data-overlay-close>${t("Cancel")}</button>
      <button type="button" class="ap-btn ap-btn--primary" id="sp-create" disabled>${icon("plus", 15)} ${t("Create & open editor")}</button>
    `,
  });
  if (!panel) return;

  panel.querySelector("[data-overlay-close]")?.addEventListener("click", () => closeOverlay(root));
  panel.querySelectorAll("[data-source]").forEach((b) =>
    b.addEventListener("click", () => {
      source = b.dataset.source;
      panel.querySelectorAll("[data-source]").forEach((x) => (x.style.borderColor = ""));
      b.style.borderColor = "var(--ap-cyan)";
      panel.querySelector("#sp-create").disabled = false;
    })
  );
  panel.querySelector("#sp-create")?.addEventListener("click", () => {
    const name = panel.querySelector("#sp-name")?.value?.trim() || t("Untitled space");
    const apt = createApartment(session.email, { name, source });
    const url = new URL(editorUrl(apt.id));
    if (source === "upload") url.searchParams.set("upload", "1");
    window.location.href = url.href;
  });
}

export function renderSpaces(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const filtered = SPACES.filter(matches);
  const counts = {
    all: SPACES.length,
    live: SPACES.filter((s) => s.status === "live").length,
    draft: SPACES.filter((s) => s.status === "draft").length,
    processing: SPACES.filter((s) => ["rendering", "analyzing"].includes(s.status)).length,
    attention: SPACES.filter((s) => s.status === "attention").length,
  };

  const content = `
    ${pageHead(t("Spaces"), t("Every apartment, project, and tour across the workspace."), `
      <button type="button" class="ap-btn" id="sp-import">${icon("upload", 15)} ${t("Import plans")}</button>
      <button type="button" class="ap-btn ap-btn--primary" id="sp-new">${icon("plus", 15)} ${t("New space")}</button>
    `)}

    <div class="ap-toolbar">
      <div class="ap-chiprow">
        ${[
          ["all", `${t("All")} · ${counts.all}`],
          ["live", `${t("Live")} · ${counts.live}`],
          ["draft", `${t("Drafts")} · ${counts.draft}`],
          ["processing", `${t("Processing")} · ${counts.processing}`],
          ["attention", `${t("Attention")} · ${counts.attention}`],
        ]
          .map(([id, label]) => `<button type="button" class="ap-chip ${state.filter === id ? "ap-chip--on" : ""}" data-filter="${id}">${label}</button>`)
          .join("")}
      </div>
      <div class="ap-toolbar__spacer"></div>
      <div class="ap-searchfield" style="width:230px;">
        ${icon("search", 15)}
        <input class="ap-input" id="sp-search" type="text" placeholder="${t("Search spaces…")}" value="${escapeHtml(state.query)}" />
      </div>
      <div class="ap-seg">
        <button type="button" class="ap-seg__btn ${state.view === "grid" ? "ap-seg__btn--on" : ""}" data-view="grid">${icon("grid", 14)}</button>
        <button type="button" class="ap-seg__btn ${state.view === "list" ? "ap-seg__btn--on" : ""}" data-view="list">${icon("filter", 14)}</button>
      </div>
    </div>

    ${
      !filtered.length
        ? `<div class="ap-panel">${emptyState({
            icon: "spaces",
            title: "No spaces match",
            body: "Try a different filter or search term — or create a new space to get started.",
            actionHtml: `<button type="button" class="ap-btn ap-btn--primary" id="sp-empty-new">${icon("plus", 15)} ${t("New space")}</button>`,
          })}</div>`
        : state.view === "grid"
          ? `<div class="ap-grid ap-grid--3" id="sp-collection">${filtered.map(spaceCard).join("")}</div>`
          : `<div class="ap-panel" id="sp-collection"><div class="ap-tablewrap ap-scroll"><table class="ap-table">
              <thead><tr><th>${t("Space")}</th><th>${t("Status")}</th><th class="ap-cell-num">${t("Rooms")}</th><th class="ap-cell-num">${t("Area")}</th><th class="ap-cell-num">${t("Sessions")}</th><th>${t("Updated")}</th><th></th></tr></thead>
              <tbody>${filtered.map(spaceRow).join("")}</tbody>
            </table></div></div>`
    }
  `;

  const el = renderShell(root, "/spaces", content);
  if (!el) return;

  el.querySelector("#sp-new")?.addEventListener("click", () => newSpaceModal(root, session));
  el.querySelector("#sp-empty-new")?.addEventListener("click", () => newSpaceModal(root, session));
  el.querySelector("#sp-import")?.addEventListener("click", () => toast(root, t("Drop floor plans anywhere, or use New space → Upload"), "info"));

  el.querySelectorAll("[data-filter]").forEach((c) =>
    c.addEventListener("click", () => {
      state = { ...state, filter: c.dataset.filter };
      renderSpaces(root);
    })
  );

  el.querySelectorAll("[data-view]").forEach((v) =>
    v.addEventListener("click", () => {
      state = { ...state, view: v.dataset.view };
      renderSpaces(root);
    })
  );

  const search = el.querySelector("#sp-search");
  search?.addEventListener("input", () => {
    state = { ...state, query: search.value };
    clearTimeout(search._t);
    search._t = setTimeout(() => {
      const pos = search.selectionStart;
      renderSpaces(root);
      const s2 = root.querySelector("#sp-search");
      s2?.focus();
      s2?.setSelectionRange(pos, pos);
    }, 240);
  });

  el.querySelector("#sp-collection")?.addEventListener("click", (e) => {
    const holder = e.target.closest("[data-space]");
    if (!holder) return;
    const sp = SPACES.find((s) => s.id === holder.dataset.space);
    if (!sp) return;
    const act = e.target.closest("[data-act]")?.dataset?.act;

    if (act === "menu") {
      e.stopPropagation();
      const pop = holder.querySelector(".ap-pop--menu");
      root.querySelectorAll(".ap-pop--menu").forEach((m) => m !== pop && m.classList.add("hidden"));
      pop?.classList.toggle("hidden");
      return;
    }
    if (act === "open") { window.location.href = editorUrl(sp.id); return; }
    if (act === "share") { toast(root, t("Tour link copied to clipboard"), "ok"); return; }
    if (act === "restage") { toast(root, t("AI restage queued for {name}", { name: sp.name }), "ok"); return; }
    if (act === "duplicate") { toast(root, t("Duplicated \"{name}\"", { name: sp.name }), "ok"); return; }
    if (act === "archive") { toast(root, t("\"{name}\" archived", { name: sp.name }), "info"); return; }
    if (act === "detail" || (!act && e.target.closest("tr[data-row]"))) detailDrawer(root, sp);
  });
}
